import { useEffect, useState } from "react";
import { FiMail, FiMapPin, FiPhone, FiUser, FiX } from "react-icons/fi";
import { apiRequest } from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";

const blankForm = {
  fullName: "",
  email: "",
  phone: "",
  marketingOptIn: false,
  status: "ACTIVE",
  addressLabel: "Primary Address",
  recipient: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  postalCode: "",
  country: "IN",
};

export default function CustomerModal({
  open,
  onClose,
  customer = null,
  onSaved,
}) {
  const { accessToken } = useAuth();
  const [form, setForm] = useState(blankForm);
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    const address = customer?.addresses?.[0];

    setForm({
      fullName: customer?.fullName || "",
      email: customer?.email || "",
      phone: customer?.phone || "",
      marketingOptIn: customer?.marketingOptIn || false,
      status: customer?.status || "ACTIVE",
      addressLabel: address?.label || "Primary Address",
      recipient: address?.recipient || customer?.fullName || "",
      addressLine1: address?.addressLine1 || "",
      addressLine2: address?.addressLine2 || "",
      city: address?.city || "",
      state: address?.state || "",
      postalCode: address?.postalCode || "",
      country: address?.country || "IN",
    });

    setError("");
  }, [open, customer]);

  const update = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const saveCustomer = async (event) => {
    event.preventDefault();

    if (!form.fullName.trim()) {
      setError("Customer name is required.");
      return;
    }

    if (!form.email.trim() && !form.phone.trim()) {
      setError("Enter at least an email address or phone number.");
      return;
    }

    try {
      setIsSaving(true);
      setError("");

      const isEditing = Boolean(customer?.id);

      const payload = isEditing
        ? {
            fullName: form.fullName.trim(),
            email: form.email.trim() || null,
            phone: form.phone.trim() || null,
            marketingOptIn: form.marketingOptIn,
            status: form.status,
          }
        : {
            fullName: form.fullName.trim(),
            email: form.email.trim() || undefined,
            phone: form.phone.trim() || undefined,
            marketingOptIn: form.marketingOptIn,
            ...(form.addressLine1.trim() &&
            form.city.trim() &&
            form.state.trim() &&
            form.postalCode.trim()
              ? {
                  addressLabel: form.addressLabel.trim() || "Primary Address",
                  recipient: form.recipient.trim() || form.fullName.trim(),
                  addressLine1: form.addressLine1.trim(),
                  addressLine2: form.addressLine2.trim() || undefined,
                  city: form.city.trim(),
                  state: form.state.trim(),
                  postalCode: form.postalCode.trim(),
                  country: form.country.trim().toUpperCase() || "IN",
                }
              : {}),
          };

      const savedCustomer = await apiRequest(
        isEditing ? `/customers/${customer.id}` : "/customers",
        {
          method: isEditing ? "PATCH" : "POST",
          accessToken,
          body: payload,
        },
      );

      onSaved?.(savedCustomer);
      onClose();
    } catch (requestError) {
      setError(requestError.message || "Unable to save customer.");
    } finally {
      setIsSaving(false);
    }
  };

  if (!open) return null;

  const isEditing = Boolean(customer?.id);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <form
        onSubmit={saveCustomer}
        className="bg-white w-full max-w-5xl max-h-[92vh] overflow-hidden rounded-3xl shadow-2xl flex flex-col"
      >
        <div className="flex items-center justify-between px-6 py-5 sm:px-8 border-b border-gray-100">
          <div>
            <h2 className="text-2xl font-bold">
              {isEditing ? "Edit Customer" : "Add Customer"}
            </h2>

            <p className="text-gray-500 mt-1">
              {isEditing
                ? "Update customer contact and account information."
                : "Create a customer profile with an optional primary address."}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="h-11 w-11 rounded-xl border border-gray-200 flex items-center justify-center hover:bg-gray-100"
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-7 sm:px-8 space-y-9">
          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <Section
            icon={<FiUser />}
            color="bg-primary-50 text-primary-700"
            title="Basic Information"
            description="Customer identity and account status"
          >
            <div className="grid lg:grid-cols-3 gap-5">
              <Input
                label="Customer Name *"
                value={form.fullName}
                onChange={(value) => update("fullName", value)}
                required
              />

              <Select
                label="Customer Status"
                value={form.status}
                onChange={(value) => update("status", value)}
                options={[
                  { value: "ACTIVE", label: "Active" },
                  { value: "DISABLED", label: "Disabled" },
                ]}
              />

              <label className="flex items-center gap-3 mt-8 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.marketingOptIn}
                  onChange={(event) =>
                    update("marketingOptIn", event.target.checked)
                  }
                  className="h-4 w-4 accent-primary-700"
                />

                <span className="text-sm font-medium text-gray-700">
                  Marketing communication consent
                </span>
              </label>
            </div>
          </Section>

          <Section
            icon={<FiPhone />}
            color="bg-blue-50 text-blue-600"
            title="Contact Information"
            description="At least one contact method is required"
          >
            <div className="grid lg:grid-cols-2 gap-5">
              <Input
                label="Mobile Number (with country code)"
                value={form.phone}
                onChange={(value) => update("phone", value)}
                placeholder="+919876543210"
              />

              <Input
                label="Email Address"
                type="email"
                value={form.email}
                onChange={(value) => update("email", value)}
                placeholder="customer@example.com"
              />
            </div>
          </Section>

          {!isEditing && (
            <Section
              icon={<FiMapPin />}
              color="bg-orange-50 text-orange-600"
              title="Primary Address"
              description="Optional. You can add more addresses from the customer profile later."
            >
              <div className="grid lg:grid-cols-2 gap-5">
                <Input
                  label="Address Label"
                  value={form.addressLabel}
                  onChange={(value) => update("addressLabel", value)}
                  placeholder="Home, Office, etc."
                />

                <Input
                  label="Recipient Name"
                  value={form.recipient}
                  onChange={(value) => update("recipient", value)}
                  placeholder={form.fullName || "Customer name"}
                />

                <Input
                  label="Address Line 1"
                  value={form.addressLine1}
                  onChange={(value) => update("addressLine1", value)}
                />

                <Input
                  label="Address Line 2"
                  value={form.addressLine2}
                  onChange={(value) => update("addressLine2", value)}
                />

                <Input
                  label="City"
                  value={form.city}
                  onChange={(value) => update("city", value)}
                />

                <Input
                  label="State"
                  value={form.state}
                  onChange={(value) => update("state", value)}
                />

                <Input
                  label="Pincode"
                  value={form.postalCode}
                  onChange={(value) => update("postalCode", value)}
                />

                <Input
                  label="Country Code"
                  value={form.country}
                  onChange={(value) => update("country", value.toUpperCase())}
                  placeholder="IN"
                />
              </div>
            </Section>
          )}

          {isEditing && (
            <div className="rounded-2xl border border-primary-100 bg-primary-50 p-5 text-sm text-primary-800">
              Address management will be available in the Customer Detail page.
            </div>
          )}
        </div>

        <div className="border-t border-gray-100 bg-gray-50 px-6 py-5 sm:px-8 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="h-11 px-6 rounded-xl border border-gray-200 bg-white"
          >
            Cancel
          </button>

          <button
            disabled={isSaving}
            type="submit"
            className="h-11 px-8 rounded-xl bg-primary-700 text-white font-medium disabled:opacity-60"
          >
            {isSaving
              ? "Saving..."
              : isEditing
                ? "Update Customer"
                : "Save Customer"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Section({ icon, color, title, description, children }) {
  return (
    <section>
      <div className="flex items-center gap-3 mb-6">
        <div
          className={`h-11 w-11 rounded-xl flex items-center justify-center ${color}`}
        >
          {icon}
        </div>

        <div>
          <h3 className="font-semibold text-lg">{title}</h3>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
      </div>

      {children}
    </section>
  );
}

function Input({
  label,
  value,
  onChange,
  type = "text",
  placeholder = "",
  required = false,
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-600 mb-2">
        {label}
      </label>

      <input
        required={required}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className="w-full h-12 rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700"
      />
    </div>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-600 mb-2">
        {label}
      </label>

      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full h-12 rounded-xl border border-gray-200 px-4 bg-white outline-none focus:border-primary-700"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}
